import { Coords } from './coords';
import { User } from './user';
import { Media } from './media';
import { Language } from './language';

import { Feature, Point, LineString } from 'geojson';
import { ReplaySubject } from 'rxjs';
import { MoteType, MoteState, MoteData, MoteContainerTypes, MoteSortType, MoteWaypoint } from './mote.data';
import { MediaEventMap } from './mote-event-type';
import { QuizQuestion } from './quiz-question';
import { DatabaseModel } from './database-model';
import { ChallengeStateMap } from './challenge-state';
import { MapUtils } from './map-utils';
import { CUSTOM_LOCATION_KEY } from './venue';
import { TourTag } from './tour-tags';

// Export motes geometry (subset of geojson geometry with only point and line)
// This means that the geometry will always have a coordinates property
export { Feature, Point, LineString };
export type Geometry = Point | LineString;

// Properties consistent with Mapbox GeoJson
export class MoteProperties {
    moteType: MoteType;
    moteState: MoteState;
    moteKey: string;
    distance: number;
    iconSet: string = "default";
    isHidden: boolean; // Hide the mote from the map and from swiper
    showQuestRoute: boolean; // Indicate if the mote is child of a quest (Treasure Hunt) route and if the partial route should be drawn
    stateChanged: string = "Yes"; // Indicate if the state changed in order to animate on new state
}

/**
 * A Mote represent content in the form of text,image, sound and video,
 * bound to a specific location.
 */
export class Mote extends DatabaseModel {
    // Mote cache to find references to mote objects
    public static moteCache: { [key: string]: Mote } = {};

    // Version of Mote data model
    public static readonly MOTE_MODEL_VERSION = 1;

    // Meta
    public version: number = 0; // Mote model 'structure' version
    /**
     *  Reference key to the project (world) this Mote belongs to
     */
    public project: string = "";
    /**
     *  Optional reference key pointing to the parent Mote (Schedule, Route, Collection or Quest) of this Mote
     */
    public parent: string;
    // @deprecated
    // public children: { [moteKey: string]: boolean } = {}; // Object containing mote children { moteKey :  true }
    public products: { [productKey: string]: boolean } = {}; // Object containing mote products { productKey :  true }

    // Tour info
    public productLocked: boolean = false; // Indicates if the products associated with this Mote is needed to access the Tour/Mote
    public price: number = 0; // Price in DKK, 0 if the tour is free
    public duration: number = 1; // The expected duration of the tour in minutes
    public length: number = 0; // Length / distance of the tour in meters
    public startPlace: string = ""; // Tour start place (default is first story address)
    public endPlace: string = ""; // Tour end place (default is final story address)
    public storyCount: number = 0; // Number of stories related to this mote, if this is a tour
    public city: string = ''; // City related to position
    public worldCityId: string = null; // World city id
    public hideFromSearch: boolean = false; // Hide tour from search results (only available in Supabase db)
    public country: string = ''; // Country related to position
    /** Accumulated app review scores (tours only) */
    public accumulatedAppReviews: { avg: number; count: number; updatedOn?: number } = null;
    /** Accumulated tour review scores (tours only) */
    public accumulatedTourReviews: { avg: number; count: number; updatedOn?: number } = null;

    // Event time stamps
    public startTime: Date = null; // Start time of event (when it should be shown)
    public endTime: Date = null; // End time of event (when it should be hidden again)

    public childCount: number = -1;  // Dynamically assigned
    public defaultState: MoteState = MoteState.LOCKED;
    // Mote order starting from 1 (0 denotes unordered)
    public order: number = 0;
    public sortBy: MoteSortType = MoteSortType.ORDER;
    public unlockMessages: string[] = [];
    public tags: string = ""; // Comma separated tag list of the mote (mainly used for tour categorization)
    public previewKey: string = "";

    // Language
    public originKey: string;               // Key of origin mote - if this is a copy or a localized mote
    public language: Language; // Language that mote is written in

    // Content
    public type: MoteType = MoteType.PLACE;
    /**
     * Title / headline of the Mote
     */
    public title: string;
    public hideTitle: boolean = false; // Hide the title on top of the picture (set to true if the title is already included in the picture)
    public isCustomTitle: boolean = false;
    /**
     * A medium or long text describing the Mote
     */
    public text: string;
    /**
     * A short text (2 lines max) acting as a teaser of the content in the Mote
     */
    public previewText: string;
    public externalLink: string = "";
    public externalLinkText: string = "";
    public motesLink: string = "";
    public motesLinkText: string = "";
    public image: Media = new Media(); // Dynamically allocated reference to first media object
    public createdBy: string = "";
    /**
     * Timestamp of when the Mote was created (milliseconds since epoch)
     */
    public createdOn: any = "";
    public updatedOn: Date = null;
    public publishedOn: Date = null;
    public keyOptimized: boolean = false;
    public deletedOn: Date = null;
    /** The position of the story (`Mote`) */
    public position: Coords = new Coords(0, 0, false);
    // Place id reference to google maps places (keep track of AI generated stories etc.)
    public placeId: string = '';
    // Human-readable name of the place from Google Places API
    public placeIdName: string = '';
    // Whether this place is a tourist attraction or POI (worth highlighting)
    public placeIdIsAttraction: boolean = false;
    public placeIdCheckedOn: number = null;
    /** Child-derived place IDs for TTD related_locations (tours with children only) */
    public relatedPlaceIds: string[] = [];
    public webflowId: string = '';
    public marketingUrl: string = '';
    public hidden: boolean = false;

    /**
     * Optional reference key to a predefined location / venue object
     */
    public venueKey: string = '';
    public rotation: number = 0;
    public mediaArray: Array<Media> = [];
    public sneakPeekMediaArray: Array<Media> = [];
    public sneakPeekHeader: string = '';
    public sneakPeekText: string = '';
    public mediaEventMap: MediaEventMap = null;
    public iconSet: string = "default";
    public quiz: QuizQuestion = null;
    public address: string = ''; // Story street and number

    // Internal control
    public doesExist: boolean = true;
    public newMote: boolean = false;
    public drawnOnMap: boolean = false;
    public completion: number = 0;
    public unlockedChildren: number = 0;

    // Dynamically updated properties
    public moteState: MoteState = MoteState.LOCKED; // Dynamically set by mote.service (based on position/user)
    public distance: number = null; // Dynamically set by mote.service (based on position)
    public unlockTime: Date = null;  // Dynamically set by mote.service in updateState (based on user)

    // Deprecated: use routeGeometry and position instead
    public geometry: Geometry = null;
    /** The exact route geometry between the previous story (`Mote`) on the tour and this one */
    public routeGeometry: [number, number][] = null;
    /**
     * Array of waypoints between the previous story (`Mote`) on the tour and this one.
     * These waypoints are used when generating the exact `routeGeometry`.
     */
    public waypoints: MoteWaypoint[] = [];
    public size: number = 0; // Size in bytes of the json document that this mote takes, dynamically set on init

    // Properties (dynamically generated in Mote model)
    public properties: MoteProperties = new MoteProperties();

    // Watch state/property changes of this mote
    private watchMoteState: ReplaySubject<Mote> = new ReplaySubject(1);
    public watchUpdates() {
        return this.watchMoteState;
    }
    public emitUpdates() {
        this.watchUpdates().next(this);
    }

    constructor(public key: string, json: MoteData = null) {
        super();
        // Init members used in endpoint in constructor instead of in declaration to workaround TSOA default / required value issue
        this.title = this.text = this.previewText = this.parent = this.originKey = "";
        this.language = Language.en;

        if(json) this.updateFromJson(json);
    }

    static fromJsonList(jsonArray, showDeleted: boolean = false): Mote[] {
        let moteArray = [];
        //console.debug("db","Got Json list from FB: ", jsonArray.length);

        for(let json of jsonArray) {

            // Get payload json from firebase and add key in data (to be compatible with current impl)
            let payload = json.payload.val();

            // Skip mote if is was deleted
            if(payload.deletedOn && !showDeleted) {
                //console.log("Got mote list with a deleted mote ", json.key);
                continue;
            }

            let cachedMote = Mote.moteCache[json.key];

            // If mote is already loaded and not changed just push it to array
            if(cachedMote && json.type != "child_changed" && json.type != "child_added") {
                // Add mote without updateing it
                moteArray.push(cachedMote);
            }
            else {
                //console.debug("db","%cMote DB Update, type:" + json.type, "color:#7fcc33; font-weight:bold;");
                // console.log("%cMote DB Update, type:" + json.type, "color:#7fcc33; font-weight:bold;", json.payload.val());
                // Reset json type - this triggers an update in the next incoming json, avoiding accumulated "child_changed" upfates
                json.type = "this_is_a_hopeless_framework";
                // Add new or updated mote to array
                moteArray.push(Mote.fromJson(json, showDeleted));
            }
        }
        return moteArray;
    }

    // Implement DatabaseModel interface (keep updateFromJson naming to be backward compatible)
    public fromJson(json: any) {
        this.updateFromJson(json);
    }

    static fromJson(json, showDeleted: boolean = false): Mote {
        if(!json || !json.key || !json.payload || !json.payload.val()) {
            console.log("bad key - mote does not exist!");
            let badMote = new Mote('badMote-' + Date.now());
            badMote.doesExist = false;
            return badMote;
        }

        // Get payload json from firebase and add key in data (to be compatible with current impl)
        let payload: MoteData = json.payload.val();

        // Return null object if mote has been marked as delete
        if(payload.deletedOn && !showDeleted) {
            return null;
        }

        payload.key = json.key;

        // Get mote object "shell" from cache if it exists
        let mote = Mote.moteCache[payload.key];

        if(!mote) {
            // Create new mote
            mote = new Mote(payload.key);
            // Save mote object in cache
            Mote.moteCache[payload.key] = mote;
        }
        // Update mote instance with new content
        mote.updateFromJson(payload);
        return mote;
    }

    public exists(): boolean {
        return this.doesExist;
    }

    public toJson(): MoteData {
        let mediaMap = {};
        this.mediaArray.forEach((media) => {
            mediaMap[media.key] = media.toJson();
        });

        let sneakPeekMediaMap = {};
        this.sneakPeekMediaArray.forEach((media, idx) => {
            sneakPeekMediaMap[media.key] = media.toJson();
            // Save order in media when saved in mote
            sneakPeekMediaMap[media.key].order = idx;
        })

        // Update length of routes and collections
        if(MoteContainerTypes[this.type] && this.routeGeometry) this.getLength();
        else this.length = 0;

        // Auto-set placeIdCheckedOn if placeId exists but checkedOn is not set
        // This handles legacy motes that have placeId but no checked timestamp
        if(this.placeId && !this.placeIdCheckedOn) {
            this.placeIdCheckedOn = Date.now();
        }

        // Map mote to DB object
        let json: MoteData = {
            version: Mote.MOTE_MODEL_VERSION,
            key: this.key,
            type: this.type,
            createdBy: this.createdBy,
            createdOn: this.createdOn,
            updatedOn: this.updatedOn,
            publishedOn: this.publishedOn,
            keyOptimized: this.keyOptimized,
            parent: this.parent,
            products: this.products,
            productLocked: this.productLocked,
            duration: this.duration,
            storyCount: this.storyCount,
            startPlace: this.startPlace,
            endPlace: this.endPlace,
            city: this.city,
            worldCityId: this.worldCityId,
            country: this.country,
            startTime: (this.startTime) ? this.startTime.getTime() : null,
            endTime: (this.endTime) ? this.endTime.getTime() : null,
            length: this.length,
            price: this.price,
            project: this.project,
            language: this.language,
            originKey: this.originKey,
            defaultState: this.defaultState,
            order: this.order,
            sortBy: this.sortBy,
            unlockMessages: this.unlockMessages,
            tags: this.tags,
            previewKey: this.previewKey,
            hidden: !!this.hidden,
            accumulatedAppReviews: this.accumulatedAppReviews,
            accumulatedTourReviews: this.accumulatedTourReviews,

            content: {
                title: this.title,
                hideTitle: this.hideTitle,
                isCustomTitle: this.isCustomTitle,
                text: this.text,
                previewText: this.previewText,
                externalLink: this.externalLink,
                externalLinkText: this.externalLinkText,
                motesLink: this.motesLink,
                motesLinkText: this.motesLinkText,
                media: mediaMap,
                sneakPeakMedia: sneakPeekMediaMap,
                sneakPeekHeader: this.sneakPeekHeader,
                sneakPeekText: this.sneakPeekText,
                quiz: this.quiz ? this.quiz.toJson() : null,
                address: this.address
            },

            mediaEventMap: this.mediaEventMap,
            iconSet: this.iconSet,
            position: this.position.toJson(),
            placeId: this.placeId,
            placeIdName: this.placeIdName,
            placeIdIsAttraction: this.placeIdIsAttraction,
            placeIdCheckedOn: this.placeIdCheckedOn,
            relatedPlaceIds: this.relatedPlaceIds?.length ? [...this.relatedPlaceIds] : [],
            webflowId: this.webflowId,
            marketingUrl: this.marketingUrl || '',
            venueKey: this.venueKey,
            rotation: this.rotation,
            geometry: this.geometry,
            routeGeometry: this.routeGeometry,
            waypoints: this.waypoints,
        };
        return json;
    }

    public updateFromJson(json: MoteData) {
        // Test if this mote seems to be deleted just now
        if(!json.createdOn && !json.content) {
            // Clean up cache - no reason to have this deleted mote in the cache
            delete Mote.moteCache[json.key];
            console.log("Deleted mote detected. Remove from cache: " + json.key);
            return;
        }

        // Nazi check content from DB
        if(json.content == null) {
            console.error("Hmmmm.. this Mote has no content. Key:'" + json.key + "' json:", json);
        }

        // Convert mediaEventMap to object if it is interpreted as array by firebase
        if(Array.isArray(json.mediaEventMap)) {
            json.mediaEventMap = json.mediaEventMap.reduce((acc, cur, i) => {
                acc[i] = cur;
                return acc;
            }, {})
        }

        // Meta
        this.version = json.version || 0;
        this.type = json.type;
        this.createdBy = json.createdBy || "";
        this.createdOn = json.createdOn || "";
        this.updatedOn = json.updatedOn ? new Date(json.updatedOn) : (json.createdOn ? new Date(json.createdOn) : new Date());
        this.publishedOn = json.publishedOn ? new Date(json.publishedOn) : null;
        this.keyOptimized = json.keyOptimized || false;
        this.deletedOn = json.deletedOn ? new Date(json.deletedOn) : null;
        this.parent = json.parent || "";
        this.project = json.project || "";
        this.language = json.language || Language.en;
        this.originKey = json.originKey || "";
        this.products = json.products || {};
        this.productLocked = json.productLocked || false;
        this.price = json.price || 0;
        this.length = json.length || 0;
        this.duration = (Array.isArray(json.duration) ? json.duration[0] : json.duration) || 0;
        this.storyCount = json.storyCount || 0;
        this.startPlace = json.startPlace || "";
        this.endPlace = json.endPlace || "";
        this.startTime = (json.startTime) ? new Date(json.startTime) : null;
        this.city = json.city || '',
        this.worldCityId = json.worldCityId || null,
        this.hideFromSearch = json.hideFromSearch || false,
        this.country = json.country || ''
        this.endTime = (json.startTime) ? new Date(json.endTime) : null;
        this.hidden = !!json.hidden;
        this.accumulatedAppReviews = json.accumulatedAppReviews || null;
        this.accumulatedTourReviews = json.accumulatedTourReviews || null;

        this.type = json.type || MoteType.PLACE;
        this.iconSet = json.iconSet || "default";
        this.properties.iconSet = this.iconSet;
        this.defaultState = json.defaultState || MoteState.LOCKED;
        this.order = json.order || 0;
        this.sortBy = json.sortBy || MoteSortType.ORDER;
        this.unlockMessages = json.unlockMessages || [];
        this.tags = json.tags || "";
        this.position = Coords.fromJson(json.position);
        this.placeId = json.placeId || "";
        this.placeIdName = json.placeIdName || "";
        this.placeIdIsAttraction = json.placeIdIsAttraction || false;
        this.webflowId = json.webflowId || "";
        this.marketingUrl = json.marketingUrl || "";

        // Backwards compatibility: infer if this old mote has been checked
        if (json.placeIdCheckedOn === undefined || json.placeIdCheckedOn === null) {
            // If this old mote has ANY place data, it means it's been checked
            // Use createdOn date as a proxy for when it was checked
            this.placeIdCheckedOn = (json.placeId || json.placeIdName) ? (json.createdOn || Date.now()) : null;
        } else {
            this.placeIdCheckedOn = json.placeIdCheckedOn;
        }

        this.relatedPlaceIds = Array.isArray(json.relatedPlaceIds) ? [...json.relatedPlaceIds] : [];
        
        this.venueKey = json.venueKey || CUSTOM_LOCATION_KEY;
        this.rotation = json.rotation || 0;
        this.size = JSON.stringify(json).length * 2; // The size of a JavaScript string is always 2 bytes per character
        this.mediaEventMap = json.mediaEventMap || null;
        this.previewKey = json.previewKey || '';

        // Content
        if(json.content) {
            this.title = json.content.title || "";
            this.hideTitle = json.content.hideTitle || false;
            this.isCustomTitle = json.content.isCustomTitle || false;
            this.text = json.content.text || "";
            this.previewText = json.content.previewText || "";
            this.externalLink = json.content.externalLink || "";
            this.externalLinkText = json.content.externalLinkText || "";
            this.mediaArray = this.updateMainMedia(json.content.media);
            this.sneakPeekMediaArray = this.updateSnakePeakMedia(json.content.sneakPeakMedia);
            this.sneakPeekHeader = json.content.sneakPeekHeader || "",
            this.sneakPeekText = json.content.sneakPeekText || "",
            this.quiz = json.content.quiz ? new QuizQuestion(json.content.quiz) : null,
            this.address = json.content.address || ''
        }

        // Save full geometry object in mote
        this.geometry = json.geometry || null;
        this.routeGeometry = json.routeGeometry || null;
        this.waypoints = json.waypoints || null;
        this.updateProperties();
    }

    private updateProperties() {
        this.properties.moteKey = this.key;
        this.properties.moteType = this.type;
        this.properties.distance = this.distance;
        this.properties.moteState = this.moteState;
    }

    private updateSnakePeakMedia(json): Media[] {
        return this.updateMedia(json);
    }

    private updateMainMedia(json): Media[] {
        let mediaArray = this.updateMedia(json);
        // Assign media with order 0 to "image"
        let mainImage = mediaArray.find((media) => media.order == 0);
        if(mainImage) this.image = mainImage;
        return mediaArray;
    }

    private updateMedia(json): Media[] {
        let mediaArray = [];

        if(!json) return mediaArray;

        // Tet if json is a media map or an array
        // TODO: Delete legacy - if this is legacy structure, convert media array from json to Media object array
        if(Array.isArray(json)) {
            for(let idx in json) {
                let media = new Media(json[idx], json[idx].key);
                mediaArray.push(media);
            }
        }
        else {
            // The json is up to data and contains a media map
            mediaArray = Object.keys(json).map(mediaKey => new Media(json[mediaKey], mediaKey));
        }

        mediaArray.sort((mediaA, mediaB) => mediaA.order - mediaB.order);
        // Make sure media always contains project key from mote
        mediaArray.forEach( media => media.project = this.project );
        this.image.project = this.project;

        return mediaArray;
    }

    /**
     * Update the position and route of the mote with data from the feature
     */
    public fromFeature(feature: Feature<Point> | Feature<LineString>): Mote {

        if(!feature.geometry || !feature.geometry.coordinates || feature.geometry.coordinates.length <= 0) {
            console.warn("No geometry data in feature, ignore mote update", this);
            return this;
        }

        // Set Geometry as any for db (old db structure)
        this.geometry = feature.geometry;

        // Update mote route from feature
        if(feature.geometry.type == 'LineString') {
            this.routeGeometry = feature.geometry.coordinates as [number, number][];
            // Update position of route as first point of route
            let firstPos = this.routeGeometry[0];
            this.position = new Coords(firstPos[1], firstPos[0]);
        }

        // Update position from 'Point' feature
        if(feature.geometry.type == 'Point') {
            this.position.long = feature.geometry.coordinates[0];
            this.position.lat = feature.geometry.coordinates[1];
        }

        return this;
    }

    /**
     * Convert mote geometry to geo json feature and carry mote content in properties
     */
    public asGeoJson(): Feature<Point> | Feature<LineString> {
        let feature: Feature<any> = {
            type: 'Feature',
            properties: this.toJson(),
            id: this.key,
            geometry: this.getGeometry()
        };
        return feature;
    }

    /**
     * Get geometry as geo json (route geometry as number array or point as coordinates)
     */
    public getGeometry(getRoute: boolean = false, hideRoutes: boolean = false): any {
        let hasRoute = (this.type == MoteType.ROUTE || this.type == MoteType.QUEST || getRoute);
        return {
            type: hasRoute && !hideRoutes ? 'LineString' : 'Point',
            coordinates: hasRoute && !hideRoutes ? this.getRoute() : this.position.longLat()
        }
    }

    /**
     * Get the route of the mote as an array of number tuples
     * Return route from the old geometry db entry if the mote was not converted yet
     */
    public getRoute(): [number, number][] {
        // Return route (new db structure)
        if(this.routeGeometry) return this.routeGeometry;
        // Return route (legacy db structure)
        if(this.geometry && this.geometry.coordinates) return this.geometry.coordinates as [number, number][];
        return [];
    }

    public setPosition(position: Coords) {
        this.position = position;
        // Deprecated
        // this.geometry.coordinates = position.longLat();
    }

    public updateDistance(userPos: Coords) {
        // No reason to calculate distance if position is invalid
        if(!userPos || !userPos.isValid) {
            this.distance = null;
            return;
        }

        this.distance = MapUtils.calcDistance(userPos.lat, userPos.long, this.position.lat, this.position.long);
        this.properties.distance = this.distance;
    }

    public getLength(points: any[] | any = this.routeGeometry): number {
        if(!points || !Array.isArray(points)) return 0;
        let coords: Coords[] = points.map((point) => new Coords(point[1], point[0]));
        return this.length = Coords.calcDistanceInCoordsArray(coords) || 0;
    }

    public getDurationHours() {
        const hours = Math.floor(this.duration / 60);
        const minutes = this.duration % 60;
        return (minutes > 30) ? (hours + 1) : hours;
    }

    public getEstimatedDuration(children: Mote[]): number {
        const timePerChild = 3;
        const kmPerHour = 5;
        let walkingDuration = this.length / 1000 * 60 / kmPerHour; // Time = distance (in km) * 60 / speed
        return this.duration = walkingDuration + children.length * timePerChild;
    }

    public setMoteState(moteState: MoteState): void {
        if(this.moteState != moteState) this.properties.stateChanged = "Yes";
        this.properties.moteState = moteState;
        this.moteState = moteState;
    }

    public updateState(user: User, userRadius: number, activeRouteKey: string = "", exclusiveRouteMode: boolean = false): MoteState {
        let state;
        let isCollection = (this.type == MoteType.COLLECTION || this.type == MoteType.ROUTE);
        let isTheActiveRoute = (this.key == activeRouteKey);

        // Inactive state (for routes/collections not currently the active experience)
        if(isCollection && !isTheActiveRoute) {
            state = MoteState.INACTIVE;
        }
        // Inactive state (for all motes not children of the active route when exclusive route mode is enabled)
        else if(exclusiveRouteMode && this.parent != activeRouteKey && !isTheActiveRoute) {
            state = MoteState.INACTIVE;
        }
        // Locked Mote
        else if(this.defaultState == MoteState.UNLOCKED) {
            // Mark mote available (if user already unlocked this will be overwritten)
            state = MoteState.UNLOCKED;
        }
        // Available Mote
        else if(this.distance != null && this.distance <= userRadius) {
            state = MoteState.AVAILABLE;
        }
        else {
            state = MoteState.LOCKED;
        }

        if(user) {
            // Unlocked Mote
            for(let unlockObject of user.unlockedMotes) {
                if(unlockObject.moteKey == this.key && state != MoteState.INACTIVE) {
                    state = MoteState.UNLOCKED;
                    this.unlockTime = new Date(unlockObject.createdOn);
                    break;
                }
            }
            // Update quest child properties (map visibility) if needed
            this.updateShowQuestRoute(user.challengeStateMap);
        }

        // console.log("Is loved", this.isLoved);
        this.setMoteState(state);
        //this.setMoteState(MoteState.AVAILABLE);
        return this.moteState;
    }

    /**
     * Determine if Quest child mote should be shown on the map
     * Returns true if user challenge level/stage is sufficient to see the mote and false if not
     */
    private showQuestRoute(challengeMap: ChallengeStateMap): boolean {
        if(!challengeMap) return false;
        // Get points give for this question in the challenge / quest and mark as solved if any points was given
        let level = challengeMap.getChallengeLevel(this.quiz.challenge) + 1;
        // Return true if user reached the level of this mote
        return (level >= this.order);
    }

    /**
     * Update showQuestRoute / isHidden properties of mote depending on user challenge map
     */
    private updateShowQuestRoute(challengeMap: ChallengeStateMap): void {
        if(!this.quiz) return;
        // Get the parent of the mote (this should be safe as parent should be in cache)
        let parent = this.getParent();
        // Do nothing for motes not children of Quest
        if(!parent || parent.type != MoteType.QUEST) return;

        // Update mote / route visibility state based on challenge state
        let showQuestMote = this.showQuestRoute(challengeMap);
        // Show quest place route
        this.properties.showQuestRoute = showQuestMote;
        // Un-hide quest place
        this.properties.isHidden = !showQuestMote;
    }

    public getParent(): Mote {
        return Mote.moteCache[this.parent];
    }

    public unlockMote() {
        this.setMoteState(MoteState.UNLOCKED);
    }

    public typeToString(capitalize: boolean = false): string {
        let type = MoteType[this.type].toLowerCase() || "Undefined";
        if(type == 'place') type = 'Mote'; // Use Mote terminlogy for consistency
        if(capitalize) type = type.charAt(0).toUpperCase() + type.slice(1);
        return type;
    }

    public toString(): string {
        return "\nMote object \nKey: '" + this.key + "'\nType: " + this.typeToString();
    }

    public canHaveChildren(): boolean {
        return MoteContainerTypes[this.type];
    }

    public static genKeyFromTitle(title: string) {
        return DatabaseModel.genKeyFromTitle(title);
    }

    public getTourTagList(): TourTag[] {
        let tagList = this.tags ? this.tags.replace(/\s/g, '').split(',') : [];
        return tagList.map((tag) => tag as TourTag);
    }

    public setTagsFromList(tagList: TourTag[]) {
        this.tags = tagList.join(', ');
    }
}
